import { Gender, RequestFeatures } from './features';
import { RequestFeaturesV2 } from './features2';
import { Features, MaskedDate } from 'controls';
import { from } from 'rxjs';
import { PersonNameDictionary } from './dictionaries/person-name.dictionary';
import { BirthPlaceDictionary, DictionaryForeignBirthPlace } from './dictionaries/birthplace.dictionary';
import { AddressDictionary } from './dictionaries/address.dictionary';
import { UTCDate } from 'types';
import { IBirthplaceDictionary } from './dictionaries/couchdb.birthplace.dictionary';

export class Birthplace {
    full: string;
    russian: string;
    latin: string;
    begin: UTCDate | null;
    end: UTCDate | null;
    origin: 'UNKNOWN' | 'FOREIGN' | 'RUSSIAN' = 'UNKNOWN';
}

export const firstNames$ = from(PersonNameDictionary.firstNames.map(_ => _.sex === 0
    ? { gender: Gender.Male, value: _.russian }
    : { gender: Gender.Female, value: _.russian }));
export const middleNames$ = from(PersonNameDictionary.middleNames.map(_ => _.sex === 0
    ? { gender: Gender.Male, value: _.russian }
    : { gender: Gender.Female, value: _.russian }));
const birthplaces$ = from(BirthPlaceDictionary.foreignBirthPlaces.map(_ => {
    const res = new Birthplace();
    res.full = _.full;
    res.latin = _.latin;
    res.russian = _.russian;
    res.begin = _.from;
    res.end = _.to;
    res.origin = 'FOREIGN';
    return res;
}).concat(BirthPlaceDictionary.russianBirthPlaces.map(_ => {
    const res = new Birthplace();
    res.end = res.begin = null;
    res.latin = '';
    res.russian = _.printable;
    res.full = _.full;
    res.origin = 'RUSSIAN';
    return res;
})));

const today = UTCDate.today(new Date());
function isActualCountry(fbp: DictionaryForeignBirthPlace): boolean {
    return fbp.type === 0
        && (fbp.from === null || fbp.from < today)
        && (fbp.to === null || fbp.to > today);
}
const addressCountries = from(BirthPlaceDictionary.foreignBirthPlaces.filter(_ => isActualCountry(_)).map(c => c.russian));
const addressAdmistrativeDivisions = from(AddressDictionary.subjects.map(s => s.name));

const latinBirthPlaceExtractor = (_: Birthplace) => _.latin;
const russianBirthPlaceExtractor = (_: Birthplace) => _.russian;

// Export
export class UserModelDict {
    private readonly USSR: string = "USSR";
    private readonly RUSSIA: string = "RUSSIA";
    private readonly USSR_START_DATE: UTCDate = new UTCDate(1922, 11, 30);
    private readonly USSR_END_DATE: UTCDate = new UTCDate(1992, 1, 6);
    private readonly DEFAULT_DATE: UTCDate = new UTCDate(2001, 0, 1);

    constructor(kladr: IBirthplaceDictionary) {
        const sourceKLADR = (prefix: string) => kladr.searchByFullName(prefix, 10);

        // FeaturesV1 options
        Features.suggestedOptions(RequestFeatures,
            (x: RequestFeatures) => x.applicant.personIdentity.fullName.firstName,
            (x: RequestFeatures, prefix: string) => Features.genderedSuggestion<RequestFeatures>(firstNames$, x, _ => _.applicant.gender, prefix)
        );
        Features.suggestedOptions(RequestFeatures,
            (x: RequestFeatures) => x.applicant.personIdentity.fullName.middleName,
            (x: RequestFeatures, prefix: string) => Features.genderedSuggestion<RequestFeatures>(middleNames$, x, _ => _.applicant.gender, prefix)
        );
        Features.suggestedOptions(RequestFeatures,
            (x: RequestFeatures) => x.applicant.nameChanges.template.firstName,
            (x: RequestFeatures, prefix: string) => Features.genderedSuggestion<RequestFeatures>(firstNames$, x, _ => _.applicant.gender, prefix)
        );
        Features.suggestedOptions(RequestFeatures,
            (x: RequestFeatures) => x.applicant.nameChanges.template.middleName,
            (x: RequestFeatures, prefix: string) => Features.genderedSuggestion<RequestFeatures>(middleNames$, x, _ => _.applicant.gender, prefix)
        );
        Features.suggestedOptions(RequestFeatures,
            (x: RequestFeatures) => x.applicant.nameChanges.template.location,
            (x: RequestFeatures, prefix: string, arrayIndexes: number[]) => Features.birthplaceSuggestion<RequestFeatures>(birthplaces$, x, (r: RequestFeatures, i: number[]) => r.applicant.nameChanges[i[0]].year, russianBirthPlaceExtractor, prefix, arrayIndexes)
        );
        Features.suggestedOptions(RequestFeatures,
            (x: RequestFeatures) => x.representative.personIdentity.fullName.firstName,
            (x: RequestFeatures, prefix: string) => Features.genderedSuggestion<RequestFeatures>(firstNames$, x, _ => _.representative.gender, prefix)
        );
        Features.suggestedOptions(RequestFeatures,
            (x: RequestFeatures) => x.representative.personIdentity.fullName.middleName,
            (x: RequestFeatures, prefix: string) => Features.genderedSuggestion<RequestFeatures>(middleNames$, x, _ => _.representative.gender, prefix)
        );
        Features.suggestedOptions(RequestFeatures,
            (x: RequestFeatures) => x.representative.nameChanges.template.firstName,
            (x: RequestFeatures, prefix: string) => Features.genderedSuggestion<RequestFeatures>(firstNames$, x, _ => _.representative.gender, prefix)
        );
        Features.suggestedOptions(RequestFeatures,
            (x: RequestFeatures) => x.representative.nameChanges.template.middleName,
            (x: RequestFeatures, prefix: string) => Features.genderedSuggestion<RequestFeatures>(middleNames$, x, _ => _.representative.gender, prefix)
        );
        Features.suggestedOptions(RequestFeatures,
            (x: RequestFeatures) => x.representative.nameChanges.template.location,
            (x: RequestFeatures, prefix: string, arrayIndexes: number[]) => Features.birthplaceSuggestion<RequestFeatures>(birthplaces$, x, (r: RequestFeatures, i: number[]) => r.representative.nameChanges[i[0]].year, russianBirthPlaceExtractor, prefix, arrayIndexes)
        );
        Features.suggestedOptions(RequestFeatures,
            (x: RequestFeatures) => x.applicant.birthPlaceFull,
            (x: RequestFeatures, prefix: string) => Features.mySuggestionKLADR(sourceKLADR, prefix)
        );
        Features.suggestedOptions(RequestFeatures,
            (x: RequestFeatures) => x.applicant.birthPlaceLat,
            (x: RequestFeatures, prefix: string) => Features.birthplaceSuggestion<RequestFeatures>(birthplaces$, x, (_: RequestFeatures, __: number[]) => _.applicant.personIdentity.birthDate, latinBirthPlaceExtractor, prefix, [])
        );
        Features.suggestedOptions(RequestFeatures,
            (x: RequestFeatures) => x.applicant.birthPlaceRus,
            (x: RequestFeatures, prefix: string) => Features.birthplaceSuggestion<RequestFeatures>(birthplaces$, x, (_: RequestFeatures, __: number[]) => _.applicant.personIdentity.birthDate, russianBirthPlaceExtractor, prefix, [])
        );
        Features.suggestedOptions(RequestFeatures,
            (x: RequestFeatures) => x.representative.birthPlace,
            (x: RequestFeatures, prefix: string) => Features.birthplaceSuggestion<RequestFeatures>(birthplaces$, x, (_: RequestFeatures, __: number[]) => _.representative.personIdentity.birthDate, russianBirthPlaceExtractor, prefix, [])
        );
        Features.suggestedOptions(RequestFeatures,
            (x: RequestFeatures) => x.applicant.permAddress.country,
            (x: RequestFeatures, prefix: string) => Features.simpleSuggestion(addressCountries, prefix)
        );
        Features.suggestedOptions(RequestFeatures,
            (x: RequestFeatures) => x.applicant.permAddress.admDivision,
            (x: RequestFeatures, prefix: string) => Features.simpleSuggestion(addressAdmistrativeDivisions, prefix)
        );
        Features.suggestedOptions(RequestFeatures,
            (x: RequestFeatures) => x.applicant.tempAddress.country,
            (x: RequestFeatures, prefix: string) => Features.simpleSuggestion(addressCountries, prefix)
        );
        Features.suggestedOptions(RequestFeatures,
            (x: RequestFeatures) => x.applicant.tempAddress.admDivision,
            (x: RequestFeatures, prefix: string) => Features.simpleSuggestion(addressAdmistrativeDivisions, prefix)
        );
        Features.suggestedOptions(RequestFeatures,
            (x: RequestFeatures) => x.residenceCountry,
            (x: RequestFeatures, prefix: string) => Features.simpleSuggestion(addressCountries, prefix)
        );
        Features.suggestedOptions(RequestFeatures,
            (x: RequestFeatures) => x.applicant.secondNationality,
            (x: RequestFeatures, prefix: string) => Features.simpleSuggestion(addressCountries, prefix)
        );
        Features.defaultValue(RequestFeatures, _ => _.applicant.birthPlaceLat, Features.birthPlaceDefaults<RequestFeatures>(
            _ => _.applicant.birthPlaceRus,
            _ => _.applicant.birthPlaceRus,
            _ => _.applicant.personIdentity.birthDate,
            (full, partial, birthDate) => this.resolveLatinBirthPlace(full, partial, birthDate)
        ));

        // FeaturesV2 options
        Features.suggestedOptions(RequestFeaturesV2,
            (x: RequestFeaturesV2) => x.applicant.permAddress.address.country,
            (x: RequestFeaturesV2, prefix: string) => Features.simpleSuggestion(addressCountries, prefix)
        );
        Features.suggestedOptions(RequestFeaturesV2,
            (x: RequestFeaturesV2) => x.applicant.permAddress.address.admDivision,
            (x: RequestFeaturesV2, prefix: string) => Features.simpleSuggestion(addressAdmistrativeDivisions, prefix)
        );
        Features.suggestedOptions(RequestFeaturesV2,
            (x: RequestFeaturesV2) => x.applicant.tempAddress.address.country,
            (x: RequestFeaturesV2, prefix: string) => Features.simpleSuggestion(addressCountries, prefix)
        );
        Features.suggestedOptions(RequestFeaturesV2,
            (x: RequestFeaturesV2) => x.applicant.tempAddress.address.admDivision,
            (x: RequestFeaturesV2, prefix: string) => Features.simpleSuggestion(addressAdmistrativeDivisions, prefix)
        );
        Features.suggestedOptions(RequestFeaturesV2,
            (x: RequestFeaturesV2) => x.representative.permAddress.address.country,
            (x: RequestFeaturesV2, prefix: string) => Features.simpleSuggestion(addressCountries, prefix)
        );
        Features.suggestedOptions(RequestFeaturesV2,
            (x: RequestFeaturesV2) => x.representative.permAddress.address.admDivision,
            (x: RequestFeaturesV2, prefix: string) => Features.simpleSuggestion(addressAdmistrativeDivisions, prefix)
        );
        Features.suggestedOptions(RequestFeaturesV2,
            (x: RequestFeaturesV2) => x.representative.tempAddress.address.country,
            (x: RequestFeaturesV2, prefix: string) => Features.simpleSuggestion(addressCountries, prefix)
        );
        Features.suggestedOptions(RequestFeaturesV2,
            (x: RequestFeaturesV2) => x.representative.tempAddress.address.admDivision,
            (x: RequestFeaturesV2, prefix: string) => Features.simpleSuggestion(addressAdmistrativeDivisions, prefix)
        );
        Features.suggestedOptions(RequestFeaturesV2,
            (x: RequestFeaturesV2) => x.representative.trustRecipient.personIdentity.fullName.firstName,
            (x: RequestFeaturesV2, prefix: string) => Features.genderedSuggestion<RequestFeaturesV2>(firstNames$, x, _ => _.representative.trustRecipient.gender, prefix)
        );
        Features.suggestedOptions(RequestFeaturesV2,
            (x: RequestFeaturesV2) => x.representative.trustRecipient.personIdentity.fullName.middleName,
            (x: RequestFeaturesV2, prefix: string) => Features.genderedSuggestion<RequestFeaturesV2>(middleNames$, x, _ => _.representative.trustRecipient.gender, prefix)
        );
        Features.suggestedOptions(RequestFeaturesV2,
            (x: RequestFeaturesV2) => x.representative.trustRecipient.birthPlace,
            (x: RequestFeaturesV2, prefix: string) => Features.birthplaceSuggestion<RequestFeaturesV2>(birthplaces$, x, (_: RequestFeaturesV2, __: number[]) => _.representative.trustRecipient.personIdentity.birthDate, russianBirthPlaceExtractor, prefix, [])
        );
        Features.defaultValue(RequestFeaturesV2, _ => _.applicant.birthPlaceLat, Features.birthPlaceDefaults<RequestFeaturesV2>(
            _ => _.applicant.birthPlaceRus,
            _ => _.applicant.birthPlaceRus,
            _ => _.applicant.personIdentity.birthDate,
            (full, partial, birthDate) => this.resolveLatinBirthPlace(full, partial, birthDate)
        ));
    }

    private resolveLatinBirthPlace(full: string, partial: string, birthday: MaskedDate) {
        const brthDn: UTCDate = birthday.toUTCDate(false) ?? this.DEFAULT_DATE;
        const brthUp: UTCDate = birthday.toUTCDate(true) ?? this.DEFAULT_DATE;
        const matchedInForeignBirthPlaceList = BirthPlaceDictionary.foreignBirthPlaces.filter(r => this.doesRecordMatchBirthDate(r, brthDn, brthUp) && r.russian === partial);
        if (matchedInForeignBirthPlaceList.length > 0) {
            return matchedInForeignBirthPlaceList[0].latin;
        }
        const matchedInRussianBirthPlaceList = BirthPlaceDictionary.russianBirthPlaces.filter(d => d.printable === partial);
        if (matchedInRussianBirthPlaceList.length > 0) {
            return this.isUSSR(brthDn, brthUp) ? this.USSR : this.RUSSIA;
        }
        return null;
    }

    private doesRecordMatchBirthDate(record: DictionaryForeignBirthPlace, birthDateDn: UTCDate, birthDateUp: UTCDate): boolean {
        return (record.from === null || record.from <= birthDateUp) && (record.to === null || birthDateDn < record.to);
    }

    private isUSSR(dateDn: UTCDate, dateUp: UTCDate) {
        return this.USSR_START_DATE <= dateUp && dateDn <= this.USSR_END_DATE;
    }
}
